import { Controller } from "@hotwired/stimulus";

// Column drawdown + layout modes for the tasks board. Every column collapses
// from its header, and the board renders either as the classic single
// scrolling row or as stacked vertical lanes that fit the screen (one lane on
// phones). Stacked mode distributes the columns into real vertical stacks —
// not a grid — so collapsing a column lets everything beneath it shift up
// like a dynamic list instead of leaving a row-height gap. The arrangement is
// saved in localStorage per organization and user, so each device reopens the
// board exactly how it was left.
export default class extends Controller {
  static targets = ["column", "modeButton"];
  static values = { storageKey: String };

  // A stack needs at least this much width before another one is added.
  static STACK_MIN_WIDTH = 288;
  static MAX_STACKS = 4;

  connect() {
    this.state = this.readState();
    this.lanes = this.element.querySelector(".kanban-lanes");
    // Board order as rendered by the server; stacking reorders the DOM, so
    // this snapshot is what flattening and redistribution restore from.
    this.orderedColumns = [...this.columnTargets];
    this.stackCount = 0;
    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.layoutStacks(), 150);
    };
    window.addEventListener("resize", this.onResize);
    this.applyMode();
    this.columnTargets.forEach((column) => this.applyColumn(column));
  }

  disconnect() {
    window.removeEventListener("resize", this.onResize);
    clearTimeout(this.resizeTimer);
  }

  // Columns swapped in by Turbo arrive after connect; keep the order snapshot
  // pointing at live nodes and re-apply their stored collapse state.
  columnTargetConnected(column) {
    if (this.orderedColumns) {
      const index = this.orderedColumns.findIndex(
        (existing) => existing.dataset.stage === column.dataset.stage
      );
      if (index >= 0) {
        this.orderedColumns[index] = column;
      } else {
        this.orderedColumns.push(column);
      }
    }
    if (this.state) this.applyColumn(column);
  }

  toggle(event) {
    const column = event.currentTarget.closest("[data-board-layout-target='column']");
    if (!column) return;
    const stage = column.dataset.stage;
    this.state.collapsed[stage] = !this.state.collapsed[stage];
    this.applyColumn(column);
    this.save();
  }

  setMode(event) {
    this.state.mode = event.params.mode === "row" ? "row" : "stack";
    this.applyMode();
    this.save();
  }

  collapseAll() {
    this.setAll(true);
  }

  expandAll() {
    this.setAll(false);
  }

  setAll(collapsed) {
    this.columnTargets.forEach((column) => {
      this.state.collapsed[column.dataset.stage] = collapsed;
      this.applyColumn(column);
    });
    this.save();
  }

  applyMode() {
    this.element.dataset.layout = this.state.mode;
    if (this.state.mode === "stack") {
      this.layoutStacks();
    } else {
      this.flattenLanes();
    }
    this.modeButtonTargets.forEach((button) => {
      const active = button.dataset.boardLayoutModeParam === this.state.mode;
      button.classList.toggle("board-mode-active", active);
      button.setAttribute("aria-pressed", active.toString());
    });
  }

  // Distribute the columns round-robin into N vertical stacks sized to the
  // available width. Round-robin keeps the top of the board reading
  // left-to-right in board order, while columns in the same stack sit under
  // each other and shift up when one above them is drawn down.
  layoutStacks() {
    if (this.element.dataset.layout !== "stack" || !this.lanes) return;
    const count = Math.max(
      1,
      Math.min(
        this.constructor.MAX_STACKS,
        Math.floor(this.lanes.clientWidth / this.constructor.STACK_MIN_WIDTH)
      )
    );
    if (count === this.stackCount) return;

    this.flattenLanes();
    const stacks = Array.from({ length: count }, () => {
      const stack = document.createElement("div");
      stack.className = "kanban-stack";
      return stack;
    });
    this.orderedColumns.forEach((column, index) => {
      stacks[index % count].appendChild(column);
    });
    stacks.forEach((stack) => this.lanes.appendChild(stack));
    this.stackCount = count;
  }

  // Restore the flat row: move columns back into the lanes container in
  // server-rendered order and drop the stack wrappers.
  flattenLanes() {
    if (!this.lanes) return;
    this.orderedColumns.forEach((column) => this.lanes.appendChild(column));
    this.lanes.querySelectorAll(".kanban-stack").forEach((stack) => stack.remove());
    this.stackCount = 0;
  }

  applyColumn(column) {
    const collapsed = !!this.state.collapsed[column.dataset.stage];
    column.dataset.collapsed = collapsed.toString();
    const header = column.querySelector(".kanban-colhead");
    if (header) header.setAttribute("aria-expanded", (!collapsed).toString());
  }

  readState() {
    const fallback = { mode: "stack", collapsed: {} };
    try {
      const raw = window.localStorage.getItem(this.storageKeyValue);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      return {
        mode: parsed.mode === "row" ? "row" : "stack",
        collapsed:
          parsed.collapsed && typeof parsed.collapsed === "object" ? parsed.collapsed : {},
      };
    } catch {
      return fallback;
    }
  }

  save() {
    try {
      window.localStorage.setItem(this.storageKeyValue, JSON.stringify(this.state));
    } catch {
      // Private browsing or full storage: the board still works, it just
      // won't remember the arrangement.
    }
  }
}
